import { UseLoadingAction } from "@/common/composition/loading";
import { UseTabs } from "@/common/composition/use-tabs";
import { ICONS } from "@/common/icons.map";
import {
  InformationSensitivityType,
  InformationType,
  InformationTypeTree,
  SensitivityLevelHelper,
} from "@/common/models";
import { SharedState } from "@/common/state";
import { is_regulated_parent } from "@/common/state/infotypes";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";
import { delayBy, group_count_by, once, sortBy, uniqby } from "@/fp";
import { ref, Ref, computed, watch } from "@vue/composition-api";
import "./api.mock";
import * as api from "./api";
import { ApiResult } from "@/common/api/ApiResult.model";
import { UseModal } from "@/components/root/modal";

const [loading, set_loading] = UseLoadingAction<"init" | "update">(
  false,
  "init"
);

type CustomizationInformationType = InformationType & {
  child_count?: number;
  deleted?: boolean;
  infoTypeSubCategoryId?: string;
  path?: string[];
};

const selected_l1_id = ref("");
const selected_l1_name = ref("");
const all_labels: Ref<CustomizationInformationType[]> = ref([]);

const selected_l1 = computed(() => {
  if (!selected_l1_id.value) return null;
  return all_labels.value.find((t) => t.id === selected_l1_id.value);
});
const sensitivity_level_for_label = (
  label?: CustomizationInformationType | null
) =>
  label
    ? label.displaySensitivityLevel ??
      label.sensitivityLevel ??
      SensitivityLevelHelper.floatToInt(label.sensitivity)
    : null;
const selected_l1_sensitivity_level = computed(() =>
  sensitivity_level_for_label(selected_l1.value)
);
const selected_l1_sensitivity_name = computed(() => {
  const level = selected_l1_sensitivity_level.value;
  return typeof level === "number"
    ? `${SensitivityLevelHelper.intToString(level)} Sensitivity`
    : "";
});

const set_selected_l1 = (l1_id: string) => {
  selected_l1_id.value = l1_id;
  if (l1_id) {
    const parent = all_labels.value.find((t: any) => t.id === l1_id);
    selected_l1_name.value = parent ? parent.labelName : "";
  } else {
    selected_l1_name.value = "";
  }
  set_display_labels();
};

const [
  info_types,
  selected_info_type,
  _set_selected_info_type,
  init_info_type,
] = UseTabs<{ id: string; name: string; icon: string }>([], 0);

const available_labels_for_category = computed(() => {
  if (selected_l1_id.value) {
    console.log("selected_l1_id::", selected_l1_id.value);

    const group: any = selected_l1.value;
    // A synthesised group collects labels from across the content tree, so its
    // members keep the parentId / infoTypeSubCategoryId of the real L1 they
    // were detected under - nothing anywhere is ever re-parented onto "spi",
    // so the generic match below returns an empty board. The group is the only
    // thing that knows its own membership. Bug 24987.
    if (group && group.fake) {
      const member_ids = new Set(
        ((group.nested || []) as InformationType[]).map((d) => d.id)
      );
      return all_labels.value.filter((t) => member_ids.has(t.id));
    }

    return all_labels.value.filter((t: any) => {
      return (
        t.infoTypeSubCategoryId == selected_l1_id.value ||
        t.parentId == selected_l1_id.value
        /* &&
        t.infoTypeCategoryId == selected_info_type.value.id &&
        t.generation == 2 */
      );
    });
  } else {
    /*
     * Bug 24987: the Contains PII / PFI / PCI parents are not generation 1 —
     * infotypes.ts pairs its own `generation == 1` test with an id check for
     * exactly this reason. Gating on generation alone dropped all three from
     * the board even once they were present in `all_labels`.
     */
    return all_labels.value.filter(
      (t: any) =>
        t.infoTypeCategoryId == selected_info_type.value.id &&
        (t.generation == 1 || is_regulated_parent(t))
    );
  }
});

const display_labels: Ref<Array<{
  name: string;
  level: number;
  level_name: string;
  items: CustomizationInformationType[];
}>> = ref([]);

const items_for_level = (level: number): CustomizationInformationType[] => {
  return available_labels_for_category.value.filter(
    (f) => (f.displaySensitivityLevel ?? f.sensitivityLevel) === level && !f.deleted
  );
};

const set_display_labels = () => {
  display_labels.value = ["None", "Low", "Medium", "High"]
    .map((level, index) => {
      return {
        level: index,
        // @ts-ignore
        items: items_for_level(index),
        level_name: level,
        name: index == 0 ? "No Sensitivity" : `${level} Sensitivity`,
      };
    })
    .reverse();
};

const set_selected_info_type = (type: {
  id: string;
  name: string;
  icon: string;
}) => {
  _set_selected_info_type(type);
  set_selected_l1("");
  set_display_labels();
};

const update_label_sensitivity = async (
  label_id: string,
  sensitivity_level: number
) => {
  // @ts-ignore
  const label: InformationTypeTree = {
    ...SharedState.allInfoTypes.value[label_id]!,
  };
  // @ts-ignore
  if (label.fake || !((label.path as string[]) || []).length) {
    // A synthesised group has no row on the server and no walkable path:
    // update_info_type_in_tree reads path.length unguarded and would throw, and
    // the id would go out on the PATCH as the literal string "spi". The group's
    // level is derived from its members, so it is changed by moving them.
    // Re-deriving display_labels snaps any card that slipped through back into
    // its real column. Bug 24987.
    set_display_labels();
    return false;
  }
  SharedState.update_info_type_in_tree({
    ...label,
    sensitivity: SensitivityLevelHelper.intToFloat(sensitivity_level),
    sensitivityLevel: sensitivity_level,
    displaySensitivityLevel: sensitivity_level,
  });
  populate_labels({ label_id, sensitivity_level });
  set_display_labels();
  const res = await api.change_label_sensitivity([
    {
      id: label_id,
      sensitivity: SensitivityLevelHelper.intToFloat(sensitivity_level),
      sensitivityType: label.sensitivityType,
    },
  ]);
  if (!res.success) {
    // revert optimistic update
    SharedState.update_info_type_in_tree(label);
    populate_labels();
    set_display_labels();
    UseToast().show({
      type: ToastType.ALERT,
      msg: "Failed to update sensitivity level :(",
      duration: 3000,
    });
  }
  return res.success;
};

const create_label = async (payload: {
  sensitivity: number;
  sensitivityType: string;
  name: string;
  desc: string;
  parentId: string;
}): Promise<ApiResult<string>> => {
  console.log("create_label payload ::", payload);
  const code = SharedState.allInfoTypes.value[payload.parentId].code;
  const res = await api.create_new_content_label({
    ...payload,
    code,
    infoTypeCategoryId: payload.parentId,
  });

  if (res.success) {
    const the_new_label = {
      ...payload,
      id: res.value,
      path: [payload.parentId],
      description: payload.desc,
      regulationType: "None",
      infoTypeCategoryId: payload.parentId,
      code,
    };
    SharedState.add_custom_content_label(the_new_label);
    populate_labels();
    set_display_labels();
  } else {
    UseToast().show({
      type: ToastType.ALERT,
      msg: "Failed to create your custom label..",
      duration: 3000,
    });
  }
  // TODO: refresh the displayed items.
  // @ts-ignore
  return res;
};
/* 
 the gap is in
 -  populate_labels()
 - set_display_labels()

*/
const populate_labels = (p?: {
  label_id: string;
  sensitivity_level: number;
}) => {
  const own_sensitivity_level = (label: InformationType) =>
    SensitivityLevelHelper.floatToInt(label.sensitivity);
  const pii_items = SharedState.PII.L1().map((d) => {
    return {
      ...d,
      displaySensitivityLevel: own_sensitivity_level(d),
      child_count: d.nested.length,
    };
  });

  /**
   * "Sensitive Personal Information" is synthesised client-side inside
   * loadInfoTypes, so it carries none of the fields a server-issued L1 has and
   * PII.L1() - which filters on labelCategory - never returns it. That is why
   * this screen has only ever shown Personal Documentation, Healthcare
   * Information and Healthcare & Medical Documentation. Bug 24987.
   *
   * It is hydrated here rather than in the tree on purpose. PII.L1() also feeds
   * three overview dashboards - each of which already hand-builds its own
   * "Sensitive Personal Information" panel into a grid that fits exactly four -
   * and the detection GraphQL query, which would receive the literal string
   * "spi" where a label GUID is expected. This screen is the only consumer that
   * wants the group as a card, so the shell stays exactly as fake as it is
   * today for the other eight.
   *
   * Found by scanning the tree rather than through PII.all(), which is
   * `info_types_tree.value[6]` - a hardcoded index into an array whose length
   * depends on how many gen-0 content labels the server returns for this org.
   * Personal is appended last, so that index is only correct for orgs with
   * exactly six of them, and a wrong index here fails silently: `.find` returns
   * undefined and the card simply never appears.
   */
  let spi_group: any = null;
  let spi_parent_id = "";
  (SharedState.info_types_tree.value || []).forEach((l0: any) => {
    const hit = ((l0 && l0.nested) || []).find((d: any) => d.id === "spi");
    if (hit && !spi_group) {
      spi_group = hit;
      // taken from the L0 that actually contains the group, not from the
      // group's own parentId - the tab filter below compares against the L0's
      // id, and two independent assumptions is one too many for something that
      // fails by silently rendering nothing
      spi_parent_id = l0.id;
    }
  });
  const spi_group_items = spi_group
    ? [
        {
          ...spi_group,
          // available_labels_for_category gates on these two, never on
          // labelCategory - without them the row reaches all_labels and still
          // renders nothing.
          generation: 1,
          infoTypeCategoryId: spi_parent_id,
          // The group's real address in the tree. Anything that walks a
          // label's path reads path[0] with no guard, so an L1-shaped row
          // without one is a crash waiting for its first consumer.
          path: [spi_parent_id, spi_group.id],
          desc:
            SharedState.info_types_desc.value[spi_group.id] ||
            "Personal information that regulations treat as sensitive, collected from across every content category.",
          // The group owns no sensitivity, and agg_children_sensitivities only
          // records which levels are present, never an aggregate - so without
          // this the card sits in "No Sensitivity" while holding CVs and
          // payslips.
          displaySensitivityLevel: ((spi_group.nested ||
            []) as InformationType[]).reduce(
            (max: number, d) => Math.max(max, own_sensitivity_level(d)),
            0
          ),
          child_count: (spi_group.nested || []).length,
        },
      ]
    : [];

  const regulated_info_types = [
    ...spi_group_items,
    ...pii_items,
    ...pii_items.flatMap((f) =>
      f.nested.map((d: InformationType) => ({
        ...d,
        displaySensitivityLevel: own_sensitivity_level(d),
      }))
    ),
  ];

  const content_labels_for_customization = Object.values(
    SharedState.allInfoTypes.value
  ).filter(
    (l) =>
      (l.labelType === "Content_label" || l.labelType === "Content_abstract") &&
      l.generation < 5
  );

  const get_parent_id = (l: any) => l.parentId || l.abstracts[0];
  const children_count_by_parent_id = group_count_by(
    get_parent_id,
    content_labels_for_customization.filter((l) => l.generation > 1)
  );

  all_labels.value = uniqby("id")(
    sortBy(
      [
        ...content_labels_for_customization.map((l) => {
          const sensitivityLevel = own_sensitivity_level(l);
          return {
            ...l,
            desc: SharedState.info_types_desc.value[l.id] || l.description,
            displaySensitivityLevel: sensitivityLevel,
            sensitivityLevel,
            child_count: children_count_by_parent_id[l.id] || 0,
          };
        }),
        ...regulated_info_types,
      ],
      "labelName",
      false
    )
  );
  if (p) {
    const { label_id, sensitivity_level } = p;
    const label = all_labels.value.find((l) => l.id === label_id);
    if (label) {
      label.sensitivity = SensitivityLevelHelper.intToFloat(sensitivity_level);
      label.sensitivityLevel = sensitivity_level;
      label.displaySensitivityLevel = sensitivity_level;
    }
  }
};

const delete_custom_label = async (label: InformationType) => {
  const res = await UseModal().showSimple({
    title: `Delete Label`,
    desc: `Are you sure you want to delete <b>${label.labelName}</b>? <br/>
    This action is irreversible.`,
    cancelBtnText: "Cancel",
    submitBtnText: "Delete",
  });
  if (res.type == "success") {
    await SharedState.delete_custom_label(label);
    populate_labels();
    set_display_labels();
    await api.delete_custom_label(label.id);
  }
};

const init = once(async () => {
  set_loading(true, "init");
  SharedState.onReady(async () => {
    if (SharedState.is_current_timeframe_before_date_of_new_piis.value) {
      await SharedState.loadInfoTypes({ is_before_date_of_new_piis: false });
    }
    const categories = SharedState.l0_known_content_info_types.value
      // @ts-ignore
      .filter((f) => !f.is_restricted)
      .map((d) => ({
        id: d.id,
        name: d.labelName,
        icon: ICONS.L0_ICONS.get(d.code),
      }));
    init_info_type(categories, 0);
    populate_labels();
    set_selected_info_type(categories[0]);
    set_loading(false, "init");
  });
});

const destroy = () => {
  //unsub_for_search();
};

export const state = {
  init,
  destroy,
  loading,
  all_labels,
  info_types,
  selected_info_type,
  set_selected_info_type,
  display_labels,
  selected_l1_id,
  selected_l1_name,
  selected_l1_sensitivity_level,
  selected_l1_sensitivity_name,
  set_selected_l1,
  // actions
  update_label_sensitivity,
  create_label,
  delete_custom_label,
};
